import { useState, useEffect } from "react";
import { apiClient } from "@/services/apiClient";

export interface PlatformActivity {
  dailyActivity: Record<string, number>;
  hourlyActivityByDay: Record<string, Record<string, number>>;
  bestHoursByDay: Record<string, number[]>;
  bestHours: number[];
  bestDays: string[];
}

export type AudienceActivityData = Record<string, PlatformActivity | null>;

export const useAudienceActivity = (clientId: string) => {
  const [data, setData] = useState<AudienceActivityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    const fetchActivity = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await apiClient.get(
          `/api/social-media/audience-activity/${clientId}`
        );
        setData(result as AudienceActivityData);
      } catch (err: unknown) {
        const apiErr = err as { response?: { data?: { error?: string } }; message?: string };
        setError(apiErr.response?.data?.error || apiErr.message || "Failed to load audience activity");
        setData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivity();
  }, [clientId]);

  return { data, isLoading, error };
};

export function getBestTimesForDay(
  activity: AudienceActivityData | null,
  platform: string,
  dayName: string
): number[] {
  if (!activity) return [];

  const platformData = activity[platform];
  if (!platformData) return [];

  const bestHours = platformData.bestHoursByDay?.[dayName.toLowerCase()];
  if (bestHours && bestHours.length > 0) {
    return bestHours;
  }

  return platformData.bestHours || [];
}

export function getDailyActivity(
  activity: AudienceActivityData | null,
  platform: string
): Record<string, number> {
  if (!activity) return {};

  const platformData = activity[platform];
  if (!platformData) return {};

  return platformData.dailyActivity || {};
}
